/**
 * मतगणना (N-PAC, 31 Oct 2026): the counting agent sends each booth's votes as the EVM shows them, with the
 * round it was counted in; the war room's board updates at once. Online only (counting halls have network
 * at the media centre; the figure must reach the war room, not wait on the phone). A booth already sent can
 * be sent again after a confirmation — the server keeps the earlier figure.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "@core/api";
import { apiErrorText } from "@core/messages";
import type { CountingInfo } from "../field/api";
import { useApp } from "../ui/context";
import { Banner, Button, Card, NumberField } from "../ui/components";

export function CountingScreen() {
  const { app, t, lang } = useApp();
  const [info, setInfo] = useState<CountingInfo | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [round, setRound] = useState("");
  const [votes, setVotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [tried, setTried] = useState(false);

  const load = useCallback(() => {
    setLoadErr(null);
    app.online((api, aid) => api.counting(aid)).then(
      (x) => setInfo(x),
      (e) => setLoadErr(e instanceof ApiError ? apiErrorText(e, lang) ?? e.message : t.netNeeded)
    );
  }, [app, lang, t]);
  useEffect(() => load(), [load]);

  const booths = useMemo(() => (info?.booths ?? []).map((b) => ({ ...b, entered: b.entered ?? b.votes != null })), [info]);
  useEffect(() => {
    if (code == null && booths.length) setCode((booths.find((b) => !b.entered) ?? booths[0]).code);
  }, [booths, code]);
  const booth = booths.find((b) => b.code === code) ?? null;
  const cands = info?.setup.candidates ?? [];
  const maxRound = info?.setup.rounds ?? null;

  const valid = (v: string | undefined) => /^\d{1,6}$/.test((v ?? "").trim());
  const roundN = round.trim() ? Number(round.trim()) : null;
  const roundBad = round.trim() !== "" && (!/^\d{1,2}$/.test(round.trim()) || roundN! < 1 || (maxRound != null && roundN! > maxRound));
  const total = cands.reduce((a, c) => a + (valid(votes[c.id]) ? Number(votes[c.id]) : 0), 0);
  const missing = cands.filter((c) => !valid(votes[c.id]));
  const fmt = (n: number) => n.toLocaleString("en-IN");

  const pick = (c: string) => {
    setCode(c);
    setVotes({});
    setErr(null);
    setOk(null);
    setConfirm(false);
    setTried(false);
  };

  const send = async () => {
    if (!booth) return;
    setTried(true);
    if (missing.length) return setErr(t.ctAllRequired);
    if (roundBad) return;
    if (booth.entered && !confirm) return setConfirm(true);
    setBusy(true);
    setErr(null);
    try {
      const r = await app.online((api, aid) => api.countBooth(aid, booth.code, roundN, Object.fromEntries(cands.map((c) => [c.id, Number(votes[c.id])]))));
      setOk(t.ctSaved(r.entry.code, fmt(r.entry.total), r.entry.corrected));
      setVotes({});
      setConfirm(false);
      setTried(false);
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? apiErrorText(e, lang) ?? e.message : t.netNeeded);
    } finally {
      setBusy(false);
    }
  };

  const doneN = booths.filter((b) => b.entered).length;
  return (
    <div data-testid="counting">
      <h2 style={{ margin: "0 0 4px" }}>🧮 {t.ctTitle}</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        {t.ctExplain}
      </p>
      {loadErr ? <Banner kind="error" action={{ label: t.reload, onClick: load }}>{loadErr}</Banner> : null}
      {info && cands.length < 2 ? <Banner kind="warn" action={{ label: t.reload, onClick: load }}>{t.ctNoSetup}</Banner> : null}
      {!info && !loadErr ? <p className="muted">…</p> : null}
      {info && cands.length >= 2 ? (
        <>
          <div className="ct-head">
            {info.setup.countDate ? <span>{t.ctDate(new Date(`${info.setup.countDate}T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "long", year: "numeric" }))}</span> : null}
            <b>{t.ctDone(doneN, booths.length)}</b>
          </div>
          <div className="field">
            <div className="label">{t.ctBoothPick}</div>
            <div className="chips ct-booths">
              {booths.map((b) => (
                <button key={b.code} type="button" className={`chip${code === b.code ? " chip-on" : ""}${b.entered ? " ct-done" : ""}`} onClick={() => pick(b.code)} data-testid={`count-booth-${b.code}`}>
                  {b.entered ? "✓ " : ""}
                  {t.boothNo} {b.code}
                  {b.entered && b.round ? <small> · {t.ctRoundShort(b.round)}</small> : null}
                </button>
              ))}
            </div>
            {booth ? <div className="hint">{booth.name}</div> : null}
          </div>

          {booth ? (
            <Card>
              {booth.entered ? (
                <Banner kind="info">
                  {t.ctEntered(booth.round, booth.enteredAt ? new Date(booth.enteredAt).toLocaleTimeString(lang === "hi" ? "hi-IN" : "en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "")}
                  <br />
                  {t.ctResend}
                </Banner>
              ) : null}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void send();
                }}
              >
                <NumberField label={t.ctRound} name="count-round" value={round} onChange={setRound} maxLength={2} hint={t.ctRoundHint(maxRound)} error={roundBad ? t.ctRoundBad(maxRound) : null} />
                <div className="ct-votes-h">{t.ctVotes}</div>
                {cands.map((c) => (
                  <NumberField
                    key={c.id}
                    label={`${c.name}${c.party ? ` (${c.party})` : ""}`}
                    name={`count-${c.id}`}
                    value={votes[c.id] ?? ""}
                    onChange={(v) => {
                      setVotes((x) => ({ ...x, [c.id]: v }));
                      setOk(null);
                    }}
                    error={(tried || (votes[c.id] ?? "") !== "") && !valid(votes[c.id]) ? t.required : null}
                  />
                ))}
                <div className={`total-line${missing.length ? "" : " ok"}`} data-testid="count-total">
                  {t.ctTotal}: <b>{fmt(total)}</b>
                </div>
                {err ? <Banner kind="error">{err}</Banner> : null}
                {ok ? (
                  <Banner kind="ok" testId="count-ok">
                    {ok}
                  </Banner>
                ) : null}
                {confirm ? (
                  <div className="stack">
                    <Banner kind="warn">{t.ctConfirm}</Banner>
                    <div className="row">
                      <Button variant="secondary" onClick={() => setConfirm(false)}>
                        {t.ctCancel}
                      </Button>
                      <Button type="submit" variant="danger" busy={busy} testId="count-confirm">
                        {t.ctConfirmYes}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button type="submit" big busy={busy} testId="count-save">
                    {t.ctSave}
                  </Button>
                )}
              </form>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
